import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faCheck,
  faTimes,
  faClock,
  faWheelchair
} from '@fortawesome/free-solid-svg-icons';
import { Atendimento, AtendimentoStatus } from '../types';
import Button from './Button';
import Badge from './Badge';

interface PatientQueueCardProps {
  atendimento: Atendimento;
  onCall?: (id: string) => void;
  onFinish?: (id: string) => void;
  onCancel?: (id: string) => void;
  showActions?: {
    call?: boolean;
    finish?: boolean;
    cancel?: boolean;
  };
  loading?: string | null;
}

const PatientQueueCard: React.FC<PatientQueueCardProps> = ({
  atendimento,
  onCall,
  onFinish,
  onCancel,
  showActions = { call: true, finish: true, cancel: true },
  loading
}) => {
  const getStatus = (): AtendimentoStatus => {
    if (atendimento.canceled_at) return 'canceled';
    if (atendimento.finished_at) return 'finished';
    if (atendimento.called_at) return 'called';
    return 'waiting';
  };

  const getStatusColor = (
    status: AtendimentoStatus
  ): 'blue' | 'green' | 'red' | 'yellow' | 'gray' | 'indigo' | 'purple' | 'pink' => {
    switch (status) {
      case 'waiting':
        return 'gray';
      case 'called':
        return 'blue';
      case 'finished':
        return 'green';
      case 'canceled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusText = (status: AtendimentoStatus): string => {
    switch (status) {
      case 'waiting':
        return 'Aguardando';
      case 'called':
        return 'Chamado';
      case 'finished':
        return 'Finalizado';
      case 'canceled':
        return 'Cancelado';
      default:
        return 'Aguardando';
    }
  };

  const formatTime = (dateString: string): string => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const status = getStatus();
  const patientName = atendimento.paciente?.nome || 'Paciente';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-3">
            <h3 className="font-medium text-gray-900 text-lg">{patientName}</h3>
          </div>

          <div className="flex gap-4 text-sm text-gray-600 mb-4">
            <div className="flex items-center space-x-1">
              <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
              <span>Chegada: {formatTime(atendimento.joined_at)}</span>
            </div>
            {atendimento.called_at && (
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon icon={faPhone} className="w-3 h-3 text-blue-500" />
                <span>Chamado: {formatTime(atendimento.called_at)}</span>
              </div>
            )}
          </div>

          {status === 'waiting' && (
            <div className="flex space-x-2">
              {showActions.call && onCall && (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={faPhone}
                  onClick={() => onCall(atendimento.id)}
                  loading={loading === atendimento.id}
                  disabled={!!loading}
                >
                  Chamar
                </Button>
              )}
              {showActions.cancel && onCancel && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={faTimes}
                  onClick={() => onCancel(atendimento.id)}
                  loading={loading === atendimento.id}
                  disabled={!!loading}
                >
                  Cancelar
                </Button>
              )}
            </div>
          )}

          {status === 'called' && (
            <div className="flex space-x-2">
              {showActions.finish && onFinish && (
                <Button
                  variant="success"
                  size="sm"
                  leftIcon={faCheck}
                  onClick={() => onFinish(atendimento.id)}
                  loading={loading === atendimento.id}
                  disabled={!!loading}
                >
                  Finalizar
                </Button>
              )}
              {showActions.cancel && onCancel && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={faTimes}
                  onClick={() => onCancel(atendimento.id)}
                  loading={loading === atendimento.id}
                  disabled={!!loading}
                >
                  Cancelar
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end space-y-2 justify-between">
          <Badge color={getStatusColor(status)} text={getStatusText(status)} />
          {atendimento.is_priority && (
            <div className="flex items-center space-x-1" title="Atendimento prioritário">
              <FontAwesomeIcon icon={faWheelchair} className="text-blue-600 w-12 h-12" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientQueueCard;
